import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import {
  Form,
  Header,
  Segment,
  Grid,
  GridColumn,
  Divider,
  GridRow,
} from 'semantic-ui-react';

import { PROJECT } from '../graphql';
import { audienceToFilterMap } from '../constants';
import optionMap from '../../../../core/components/filterable-grid/multi-filter-component/optionMap';

import {
  SelectSegments,
} from './styles';

const ProjectFilter = ({ projectId, onChange }) => {
  const [selectedSegment, setSelectedSegment] = useState('all');

  const {
    loading,
    data = {},
  } = useQuery(PROJECT, {
    variables: {
      id: projectId,
    },
    // skip: !projectId,
    // fetchPolicy: 'no-cache',
  });

  const { project = {} } = data;
  const { name: projectName = '', segments, company = {} } = project;

  const handleSegmentChange = (_, option) => {
    setSelectedSegment(option.value);
  };

  const onChanged = useCallback(onChange, [projectId]);

  const customFieldHandlers = useMemo(() => ({
    default: (attribute) => attribute.audienceAttributeOptions.map((opt) => opt.name),
    companySize: (attribute) => attribute.audienceAttributeOptions.map((opt) => optionMap[opt.name]),
    age: (attribute) => {
      const values = [];
      attribute.audienceAttributeOptions.forEach((opt) => {
        const splited = opt.name.split('-');
        const init = parseInt(splited[0], 10);
        const end = parseInt(splited[1], 10);
        for (let i = init; i <= end; i += 1) {
          values.push(i);
        }
      });
      return values;
    },
  }), []);

  useEffect(() => {
    const filterModel = {};
    let selectedSegments = segments;
    if (selectedSegment !== 'all') {
      selectedSegments = segments.filter((segment) => selectedSegment === segment.id);
    }
    (selectedSegments || []).forEach((segment) => (segment.audienceAttributes || []).forEach((attribute) => {
      const name = audienceToFilterMap[attribute.audienceAttributeType] || attribute.audienceAttributeType;
      const handler = customFieldHandlers[name] || customFieldHandlers.default;
      const values = handler(attribute);
      if (!filterModel[name]) {
        filterModel[name] = values;
      } else {
        filterModel[name] = [...filterModel[name], ...values];
      }
    }));
    return onChanged(filterModel);
  }, [selectedSegment, segments, customFieldHandlers, onChanged]);

  const segmentsOptions = [
    { key: 'all', text: 'All', value: 'all' },
    ...(segments || []).map((segment) => (
      {
        key: segment.id,
        text: segment.name,
        value: segment.id,
      }
    )),
  ];

  if (loading) {
    return <p>Loading...</p>;
  }

  if (!project.name) {
    return null;
  }

  return (
    <Segment raised>
      <Header as="h3">Project Filter</Header>
      <Divider />
      <Grid>
        <GridRow verticalAlign="middle">
          <GridColumn width={3}>
            Project:
            <b>{ projectName }</b>
          </GridColumn>
          <GridColumn width={4}>
            Workspace:
            <b>{company.name}</b>
          </GridColumn>
          <GridColumn width={5}>
            <Form.Field>
              <label>Segments </label>
              <SelectSegments options={segmentsOptions} value={selectedSegment} onChange={handleSegmentChange} />
            </Form.Field>
          </GridColumn>
        </GridRow>
      </Grid>
    </Segment>
  );
};

ProjectFilter.propTypes = {
  projectId: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default ProjectFilter;
